/**
 * SelectMultipleFilter needs to parse values from format
 * "has_all:a,b,c,d" or "a,b,c,d"
 */
export const parseSelectFilterOptions = uriComponentValue => {
  const startsWithHasAll = uriComponentValue && uriComponentValue.indexOf('has_all:') === 0;
  const startsWithHasAny = uriComponentValue && uriComponentValue.indexOf('has_any:') === 0;

  if (startsWithHasAll) {
    return uriComponentValue.substring(8).split(',');
  } else if (startsWithHasAny) {
    return uriComponentValue.substring(8).split(',');
  } else {
    return uriComponentValue.split(',');
  }
};

/**
 * Create the name of the query parameter.
 *
 * @param {String} key Key extracted from listingExtendData config.
 * @param {String} scope Scope extracted from listingExtendData config.
 */
export const constructQueryParamName = (key, scope) => {
  const prefixedKey = scope === 'meta' ? `meta_${key}` : `pub_${key}`;
  return prefixedKey.replace(/\s/g, '_');
};

/**
 * Get parameter names for search query. Extract those from config.
 * The configuration of default filters has key, which is 1-on-1 mapping
 * with the name of the query parameter. E.g. 'price'.
 *
 * @param {Object} listingFieldsConfig Custom filters are checked agains extended data config of a listing entity.
 * @param {Object} defaultFiltersConfig Configuration of default filters.
 */
export const getQueryParamNames = (listingFieldsConfig, defaultFiltersConfig) => {
  const queryParamKeysOfDefaultFilters = defaultFiltersConfig.reduce((pickedKeys, config) => {
    const { key, schemaType, scope, nestedParams } = config;
    const newKeys =
      schemaType === 'category' && nestedParams
        ? nestedParams?.map(p => constructQueryParamName(p, scope))
        : schemaType === 'listingType'
        ? [constructQueryParamName(key, scope)]
        : [key];
    return [...pickedKeys, ...newKeys];
  }, []);
  const queryParamKeysOfListingFields = listingFieldsConfig.reduce((params, config) => {
    const param = constructQueryParamName(config.key, config.scope);
    return config.filterConfig?.indexForSearch ? [...params, param] : params;
  }, []);
  return [...queryParamKeysOfDefaultFilters, ...queryParamKeysOfListingFields];
};
/**
 * Check if any of the filters (defined by filterKeys) have currently active query parameter in URL.
 */
export const isAnyFilterActive = (filterKeys, urlQueryParams, filterConfigs) => {
  const { listingFieldsConfig, defaultFiltersConfig } = filterConfigs;
  const queryParamKeys = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

  const getQueryParamKeysOfGivenFilters = (pickedKeys, key) => {
    const isFilterIncluded = filterKeys.includes(key);
    const addedQueryParamNamesMaybe = isFilterIncluded ? [key] : [];
    return [...pickedKeys, ...addedQueryParamNamesMaybe];
  };
  const queryParamKeysOfGivenFilters = queryParamKeys.reduce(getQueryParamKeysOfGivenFilters, []);

  const paramEntries = Object.entries(urlQueryParams);
  const activeKey = paramEntries.find(entry => {
    const [key, value] = entry;
    return queryParamKeysOfGivenFilters.includes(key) && value != null;
  });
  return !!activeKey;
};

/**
 * Pick initial vales for FieldSelectTree component.
 * The value object should be an object containing search params:
 * { pub_categoryLevel1: 'cats', pub_categoryLevel2: 'egyptian-mau', meta_foo: 'bar' }
 *
 * @param {String} prefix like "pub_categoryLevel"
 * @param {Object} values object literal containing level-specific info
 *
 * @returns returns properties, which have a key that starts with the given prefix.
 */
export const pickInitialValuesForFieldSelectTree = (prefix, values, isNestedEnum) => {
  const pickValuesFn = (picked, entry) => {
    const [key, value] = entry;
    const prefixIndex = key.indexOf(prefix);
    const startsWithPrefix = prefixIndex > -1;
    const slicedKey = isNestedEnum ? key.slice(prefixIndex) : `${key.slice(prefixIndex)}1`;
    return startsWithPrefix ? { ...picked, [slicedKey]: value } : picked;
  };
  const prefixCollection = Object.entries(values).reduce(pickValuesFn, {});
  return prefixCollection;
};

/**
 * This converts the category structure to the format that that's understood by SelectSingleFilter
 * and its child component: FieldSelectTree.
 *
 * @param {Array} categories contain objects with props: _id_, _name_, potentially _subcategories_.
 * @returns an array that contains objects with props: _option_, _label_ and potentially _suboptions_.
 */
export const convertCategoriesToSelectTreeOptions = categories => {
  const convertSubcategoryData = params => {
    const { id, name, subcategories } = params;
    const suboptionsMaybe = subcategories
      ? { suboptions: subcategories.map(cat => convertSubcategoryData(cat)) }
      : {};
    return { option: id, label: name, ...suboptionsMaybe };
  };

  const categoriesArray = Array.isArray(categories) ? categories : [];
  return categoriesArray.map(cat => convertSubcategoryData(cat));
};

/**
 * Check if the main search type is 'keywords'
 */
export const isMainSearchTypeKeywords = config =>
  config.search?.mainSearch?.searchType === 'keywords';

/**
 * Check if the origin parameter is currently active.
 */
export const isOriginInUse = config =>
  config.search?.mainSearch?.searchType === 'location' && config.maps?.search?.sortSearchByDistance;

/**
 * Check if the stock management is currently active.
 */
export const isStockInUse = config => {
  const listingTypes = config.listing.listingTypes;
  const stockProcesses = ['default-purchase'];
  const hasStockProcessesInUse = !!listingTypes.find(conf =>
    stockProcesses.includes(conf.transactionType.process)
  );

  // Note: these are active processes!
  return hasStockProcessesInUse;
};

// ───────────────────────────────────────────────────────────────
// Stream 3 PR-4c: nice-to-have re-ranking
// ───────────────────────────────────────────────────────────────
//
// Renters can express preferences (aesthetic tags, add-on categories
// they'd like) without those preferences narrowing the result set.
// Sharetribe returns a relevance-ordered list; we re-sort it
// client-side so listings that match more preferences float up.
//
// Per Q5 lock: total boost is capped at 3, so date/location/price
// (handled server-side by must-have filters) still dominate
// ordering — preferences nudge, they don't override.
//
// Convention: preference URL params use the `pref_` prefix to keep
// them distinct from Sharetribe-side `pub_` hard filters. The
// SearchPage reads them and threads them through
// rerankBySearchPreferences(); pref_* params are NOT forwarded to
// the Marketplace API.

export const PREFERENCE_QUERY_PARAM_PREFIX = 'pref_';

// Cap from design doc §10 Q5: each matched preference adds +1 to
// the listing's boost, total stops at 3 so preferences can't
// leapfrog a better date/location/price match by more than ~3
// positions.
export const MAX_PREFERENCE_BOOST = 3;

/**
 * Parse the preferences object out of URL search params.
 *
 * @param {Object} params  flat query-param object (from urlHelpers.parse)
 * @returns {Object}       e.g. { aesthetic: ['boho','garden'], decor: [...] }
 */
export const parseSearchPreferences = (params = {}) => {
  return Object.keys(params).reduce((acc, key) => {
    if (!key.startsWith(PREFERENCE_QUERY_PARAM_PREFIX)) return acc;
    const slug = key.slice(PREFERENCE_QUERY_PARAM_PREFIX.length);
    const raw = params[key];
    if (typeof raw !== 'string' || raw.length === 0) return acc;
    const values = raw
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    return values.length > 0 ? { ...acc, [slug]: values } : acc;
  }, {});
};

/**
 * Score a single listing against an active preferences object.
 * Returns the total number of preference matches (capped at
 * MAX_PREFERENCE_BOOST). Pure / deterministic.
 *
 * Match rules:
 *   - `aesthetic`: listing's publicData.keywords intersected with
 *      the preference values.
 *   - other tiers (decor / fnb / production / future): listing's
 *      publicData.addOnCategories[].category intersected with the
 *      preference values.
 */
export const scoreListingAgainstPreferences = (listing, preferences) => {
  if (!listing || !preferences) return 0;
  const publicData = listing?.attributes?.publicData || {};
  const keywords = Array.isArray(publicData.keywords) ? publicData.keywords : [];
  const addOnCategories = Array.isArray(publicData.addOnCategories)
    ? publicData.addOnCategories.map(row => row?.category).filter(Boolean)
    : [];

  let score = 0;
  for (const [tier, prefValues] of Object.entries(preferences)) {
    if (!Array.isArray(prefValues) || prefValues.length === 0) continue;
    const haystack = tier === 'aesthetic' ? keywords : addOnCategories;
    const matchCount = prefValues.filter(v => haystack.includes(v)).length;
    score += matchCount;
    if (score >= MAX_PREFERENCE_BOOST) return MAX_PREFERENCE_BOOST;
  }
  return score;
};

/**
 * Re-sort a listing array by descending preference score. Listings
 * with the same score retain their original (Sharetribe-returned)
 * relative order — stable sort keeps date/location/price weighting
 * intact for ties. Returns a new array; does not mutate the input.
 */
export const rerankBySearchPreferences = (listings, preferences) => {
  if (!Array.isArray(listings) || listings.length === 0) return listings || [];
  if (!preferences || Object.keys(preferences).length === 0) return listings;

  return listings
    .map((listing, originalIndex) => ({
      listing,
      originalIndex,
      score: scoreListingAgainstPreferences(listing, preferences),
    }))
    .sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;
      return a.originalIndex - b.originalIndex;
    })
    .map(({ listing }) => listing);
};

/**
 * For a results-context-bar copy block. Returns an array of
 * { tier, value, count } telling the renter how many of the
 * currently-loaded listings match each active preference. Hidden
 * counts (0 matches) are dropped — no point telling the renter
 * "0 offer catering."
 */
export const preferenceMatchSummary = (listings, preferences) => {
  if (!Array.isArray(listings) || listings.length === 0) return [];
  if (!preferences) return [];
  const summary = [];
  for (const [tier, prefValues] of Object.entries(preferences)) {
    if (!Array.isArray(prefValues)) continue;
    for (const value of prefValues) {
      const singleValue = { [tier]: [value] };
      const count = listings.filter(
        l => scoreListingAgainstPreferences(l, singleValue) > 0
      ).length;
      if (count > 0) summary.push({ tier, value, count });
    }
  }
  return summary;
};

// ─── Location display helpers (2026-07-02, audit "location spine") ──

// Full US state name → 2-letter code. Shared by the search-card city
// label here and the LocationFilter pill label.
export const US_STATE_ABBR = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', Florida: 'FL', Georgia: 'GA',
  Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA', Kansas: 'KS',
  Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD', Massachusetts: 'MA',
  Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS', Missouri: 'MO', Montana: 'MT',
  Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND',
  Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI',
  'South Carolina': 'SC', 'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX',
  Utah: 'UT', Vermont: 'VT', Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV',
  Wisconsin: 'WI', Wyoming: 'WY', 'District of Columbia': 'DC',
};

/**
 * "Goleta, California 93117, United States" → "Goleta, CA" — and,
 * critically, "777 Glen Annie Road, Goleta, California 93117, United
 * States" → "Goleta, CA" TOO. publicData.location.address can hold the
 * host's FULL street address, so the city is located by walking from
 * the END (country → state+zip → city), never by taking the first
 * segment. FAIL CLOSED: if no state segment is recognized, return ''
 * and show nothing rather than risk printing a street address on a
 * public card (hotfix 2026-07-02 — the first version leaked one).
 */
export const cityStateLabel = address => {
  if (typeof address !== 'string' || !address.trim()) return '';
  const parts = address
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  if (/united states/i.test(parts[parts.length - 1] || '')) parts.pop();
  const statePart = (parts.pop() || '').replace(/\d{5}(-\d{4})?/, '').trim();
  const abbr =
    US_STATE_ABBR[statePart] ||
    (Object.values(US_STATE_ABBR).includes(statePart) ? statePart : '');
  if (!abbr) return '';
  const city = parts.pop() || '';
  return city ? `${city}, ${abbr}` : '';
};

/**
 * Location-search radii (miles) — the ONLY radius constants; both bounds
 * producers (sidebar LocationFilter, homepage ZIP search) and the results
 * split read these. The query box extends SEARCH_RADIUS_MILES; results
 * beyond NEAR_RADIUS_MILES render under the "A bit farther away" divider.
 * Decisions: docs/design/search-extended-radius.md (owner 2026-07-02).
 */
export const NEAR_RADIUS_MILES = 25;
export const SEARCH_RADIUS_MILES = 50;

/**
 * Midpoint of a Sharetribe `bounds` URL param
 * ("neLat,neLng,swLat,swLng") → { lat, lng } | null. Drives the
 * "x mi away" distance on search cards when a location is active.
 */
export const boundsCenter = boundsParam => {
  // validUrlQueryParams parses `bounds` into an SDK LatLngBounds
  // ({ ne: {lat,lng}, sw: {lat,lng} }) — accept that shape as well as
  // the raw "neLat,neLng,swLat,swLng" string.
  if (boundsParam && typeof boundsParam === 'object') {
    const { ne, sw } = boundsParam;
    if (
      Number.isFinite(ne?.lat) &&
      Number.isFinite(ne?.lng) &&
      Number.isFinite(sw?.lat) &&
      Number.isFinite(sw?.lng)
    ) {
      return { lat: (ne.lat + sw.lat) / 2, lng: (ne.lng + sw.lng) / 2 };
    }
    return null;
  }
  if (typeof boundsParam !== 'string') return null;
  const nums = boundsParam.split(',').map(Number);
  if (nums.length !== 4 || nums.some(n => !Number.isFinite(n))) return null;
  const [neLat, neLng, swLat, swLng] = nums;
  return { lat: (neLat + swLat) / 2, lng: (neLng + swLng) / 2 };
};
